<template>
	<div>
		<support-dialog
			v-model="supportDialog"
			:full-path="`${apiUrl}${$route.fullPath}`"
			@closeDialog="supportDialog = false"
		/>

		<logout-dialog v-model="logoutDialog" />

		<v-navigation-drawer
			v-if="connected && profile.clinic"
			v-model="drawer"
			dark
			color="secondary"
			app
			permanent
			:temporary="!mini"
			:expand-on-hover="!$vuetify.breakpoint.xs"
			:mini-variant.sync="mini"
			touchless
		>
			<div class="full-height d-flex flex-column">
				<v-list>
					<v-list-item class="logo-list-item">
						<v-list-item-icon
							class="mr-3"
						>
							<v-img max-width="24" src="@/assets/img/icon-fresk-dark.svg" />
						</v-list-item-icon>

						<v-list-item-content>
							<v-list-item-title v-if="!mini" class="logo-image">
								<img src="@/assets/img/logo-white.svg" class="ml-3 logo-text-img">
							</v-list-item-title>
						</v-list-item-content>
					</v-list-item>
				</v-list>

				<v-divider />

				<navigation-list
					:items="filterBySector(topItems)"
					class="flex-grow-1"
					:notification="hasPendingNotifications"
					:email-notification="getGroupNewEmails"
					:fax-notification="hasUnreadFaxes"
					@item-click="onNavItemClick"
				/>

				<devops-clinic-indicator v-if="isDevops && $vuetify.breakpoint.xs" inline />

				<v-divider />

				<navigation-list
					:items="filterBySector(bottomItems)"
					@click:reportProblem="supportDialog = true"
					@click:logout="logoutUser"
					@click:helpCenter="goToHelpCenter"
					@click:supportTalk="callToSupportCenter"
					@item-click="onNavItemClick"
				/>
			</div>
		</v-navigation-drawer>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";

import {  prodMode } from "@/common/utils";
import { bus } from "@/main.js";
import {
	CONSULTATION_WHITELIST,
	HelpCenterLink,
	SUPPORT_PHONE,
	SUPPORT_NAME,
	SUPPORT_BUSINESS,
	SUPPORT_EMAIL
} from "@/common/const";
import { version } from "@/main";
import { API_URL } from "@/App.vue";

import SupportDialog from "../support/SupportDialog";
import LogoutDialog from "../login/LogoutDialog";

import NavigationList from "./NavigationList.vue";
import DevopsClinicIndicator from "../overlays/DevopsClinicIndicator.vue";

export default {
	name: "NavigationMenu",
	components: { SupportDialog, LogoutDialog, NavigationList, DevopsClinicIndicator },

	data() {
		return {
			drawer: true,
			mini: true,
			supportDialog: false,
			logoutDialog: false,
			oldActiveType: undefined,
			version,
			apiUrl: API_URL,
			items: {
				top: [{
					id: "conversations",
					title: "conversations",
					icon: "mdi-message-text-outline",
					route: "/conversations"
				}, {
					id: "emails",
					title: "emails",
					icon: "mdi-email-outline",
					route: "/emails"
				},
				{
					id: "fax",
					title:"fax",
					icon: "mdi-fax",
					route: "/fax"
				},
				{
					id: "text-bloc",
					title: "quick-answers",
					icon: "mdi-message-plus-outline",
					route: "/text-bloc"
				}, {
					id: "appointment-templates",
					title: "appointment-templates.page-title",
					icon: "mdi-calendar-clock",
					route: "/appointment-templates"
				}, {
					id: "library",
					title: "libraries",
					icon: "mdi-notebook-outline",
					route: "/material/2"
				}, {
					id: "consultations",
					title: "consultations",
					icon: "mdi-clipboard-check-outline",
					route: "/consultations"
				}, {
					id: "contacts",
					title: "contacts",
					icon: "mdi-account-group",
					route: "/contacts"
				}, {
					id: "configuration",
					title: "configuration",
					icon: "mdi-cog-outline",
					route: "/configuration"
				}, {
					id: "faq",
					title: "FAQ",
					icon: "mdi-frequently-asked-questions",
					route: "/faq"
				}],
				bottom: [
					{
						id: "help",
						title: "help",
						icon: "mdi-help-circle-outline",
						submenu: [
							{
								id: "helpCenter",
								title: "help-center",
								icon: "mdi-open-in-new"
							},
							{
								id: "reportProblem",
								title: "report-a-problem",
								icon: "mdi-alert"
							},
							{
								id: "supportTalk",
								title: "talk-with-the-support",
								icon: "mdi-face-agent"
							}
						]
					}, {
						id: "version",
						title: "version",
						titleParams: { version },
						subtitle: !prodMode() ? process.env.NODE_ENV : undefined,
						icon: "mdi-animation-outline",
						route: "/release-notes",
						visibilityGuard: () => !this.isFrance
					}, {
						id: "logout",
						title: "logout",
						icon: "mdi-logout"
					}]
			}
		};
	},

	computed: {
		...mapGetters("login", ["connected", "profile", "isDevops", "isAdmin", "userGroup"]),
		...mapGetters("conversation", [ "hasPendingNotifications"]),
		...mapGetters("clinic", ["isSector", "clinicData", "hasWebsite", "isFrance"]),
		...mapFields("blocGrid", ["activePostType", "loadingPosts"]),
		...mapGetters("emailInbox", ["getGroupNewEmails"]),
		...mapGetters("faxes", ["hasUnreadFaxes"]),

		appointmentFlag() {
			return this.clinicData?.appointmentFlag === true;
		},

		topItems() {
			if (!this.items) {
				return [];
			}
			return this.items.top.map((item) => {
				if (item.id === "appointment-templates") {
					return {
						...item,
						disabled: !(this.isSector("notary") && this.appointmentFlag)
					};
				}

				if (item.id === "consultations") {
					return {
						...item,
						disabled: !CONSULTATION_WHITELIST.includes(this.profile?.clinic)
					};
				}

				if (item.id === "emails") {
					return {
						...item,
						visibilityGuard: () =>
							!this.userGroup?.settings?.adminOnlyEmailInbox ||
							this.isAdmin
					};
				}

				return item;
			});
		},

		bottomItems() {
			return this.items.bottom || [];
		}
	},

	watch: {
		mini(newVal) {
			this.$emit(newVal ? "mini" : "expanded");
		}
	},

	mounted() {
		// Add click outside listener for xs breakpoint
		document.addEventListener("click", this.handleOutsideClick);
		document.addEventListener("touchstart", this.handleOutsideClick);
	},

	beforeDestroy() {
		document.removeEventListener("click", this.handleOutsideClick);
		document.removeEventListener("touchstart", this.handleOutsideClick);
	},

	methods: {
		async logoutUser() {
			this.logoutDialog = true;
		},

		filterBySector(menuItems) {
			if (!Array.isArray(menuItems)) return menuItems;

			return menuItems.filter((menuItem) => {
				return (
					!menuItem.visibilityGuard ||
					(typeof menuItem.visibilityGuard == "function" && menuItem.visibilityGuard())
				);
			});
		},

		changeNavigation(newLink) {
			if (this.oldActiveType !== undefined && this.oldActiveType != newLink) {
				this.loadingPosts = true;
				this.disableNavigation(newLink);
			}

			this.oldActiveType = newLink;
		},

		goToHelpCenter() {
			let link = "";

			switch (this.profile.locale) {
				case "fr-FR":
					link = HelpCenterLink.FR_FR;
					break;

				case "fr-CA":
					link = HelpCenterLink.FR_CA;
					break;

				case "en-CA":
					link = HelpCenterLink.EN_CA;
					break;
			}
			window.open(link, "_newtab");
		},

		async callToSupportCenter() {
			bus.$emit("start-outbound-call", { phoneNumber: SUPPORT_PHONE, patient: { fullname: SUPPORT_NAME, business: SUPPORT_BUSINESS, email: SUPPORT_EMAIL } });
		},

		onLogoClick() {
			// No-op: devops indicator is now inline in the nav on mobile
		},

		onLogoTouch() {
			// No-op: devops indicator is now inline in the nav on mobile
		},

		onNavItemClick() {
			// Keep menu mini on mobile xs breakpoint when clicking nav items
			if (this.$vuetify.breakpoint.xs) {
				this.mini = true;
			}
		},

		handleOutsideClick(event) {
			// Only on xs breakpoint when menu is expanded
			if (!this.$vuetify.breakpoint.xs || this.mini) return;

			// Check if click is outside the navigation drawer
			const drawer = this.$el.querySelector(".v-navigation-drawer");

			if (drawer && !drawer.contains(event.target)) {
				this.mini = true;
			}
		},

		goToFaq() {
			this.$router.push({ name: "FAQ" });
		}
	}
};
</script>

<style scoped>
	.logo-image {
		max-width: 100px;
		display: flex;
		align-items: center;
	}

	.logo-text-img {
		max-width: 100px;
		width: 100px;
		height: auto;
		flex-shrink: 0;
	}

	.logo-list-item ::v-deep .v-list-item__content {
		padding: 10px;
	}

	/* Fix navigation drawer height on mobile Safari */
	@media (max-width: 599px) {
		::v-deep .v-navigation-drawer {
			height: 100dvh !important;
			max-height: 100dvh !important;
		}

		::v-deep .v-navigation-drawer .v-list:first-child {
			padding-top: 0;
			padding-bottom: 0;
		}

		::v-deep .v-navigation-drawer .v-list:first-child .v-list-item {
			min-height: 24px;
			padding-top: 0;
			padding-bottom: 0;
		}

		::v-deep .v-navigation-drawer .v-list:first-child .v-list-item .v-list-item__icon {
			margin-top: 8px;
			margin-bottom: 8px;
		}
	}
</style>

<!-- Unscoped: needed because app drawer is teleported outside component scope -->
<style>
	@media (max-width: 599px) {
		.v-navigation-drawer--is-mobile,
		.v-navigation-drawer[data-app] ,
		.v-application .v-navigation-drawer {
			transition: none !important;
		}

		.v-navigation-drawer--is-mobile *,
		.v-application .v-navigation-drawer *,
		.v-application .v-navigation-drawer .v-navigation-drawer__content {
			transition: none !important;
			animation: none !important;
		}
	}
</style>
